import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { listFeaturedProducts } from "../../actions/productActions";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import Meta from "../../components/Meta";
import ProductImage from "../../components/images/ProductImage";

import {
  HABERDASHERY_SHOP_KEY,
  READY_MADE_SHOP_KEY,
  SHOPS,
  SHOP_PATHS,
  TEXTILE_SHOP_KEY,
} from "../../constants/shopMeta";
import { getProductImage } from "../../utils/productImages";
import { getProductDisplayPrice } from "../../utils/productPricing";

import "./ShopLandingScreen.css";

const HERO_IMAGE = "/images/home/hero.jpg";
const FEATURED_PRODUCT_LIMIT = 4;

const DEPARTMENTS = [
  {
    key: READY_MADE_SHOP_KEY,
    title: "Ready-Made",
    subtitle: "Replica & Archive Garments",
    description:
      "Faithful reproductions and original archive clothing from the 1900s through the 1980s. Sourced and selected for makers, collectors and costume professionals.",
    cta: "Browse Ready-Made",
    to: SHOP_PATHS[READY_MADE_SHOP_KEY],
    image: "/images/home/vintage-reproductions.jpg",
    imageAlt: "Replica and archive ready-made garments",
    accent: "#2c2c2c",
  },
  {
    key: TEXTILE_SHOP_KEY,
    title: "Textiles",
    subtitle: "Archive Fabrics",
    description:
      "Specialist fabrics by the metre, half metre or sample. Sourced with attention to weave, weight, colour and historical reference.",
    cta: "Browse Textiles",
    to: SHOP_PATHS[TEXTILE_SHOP_KEY],
    image: "/images/home/textiles.jpg",
    imageAlt: "Archive textiles and specialist fabrics",
    accent: "#5c3d2e",
  },
  {
    key: HABERDASHERY_SHOP_KEY,
    title: "Haberdashery",
    subtitle: "Buttons, Buckles, Braids & Trims",
    description:
      "Vintage and archive haberdashery with character and utility. Buttons, buckles, braids, tapes, hooks and specialist finishing details.",
    cta: "Browse Haberdashery",
    to: SHOP_PATHS[HABERDASHERY_SHOP_KEY],
    image: "/images/home/haberdashery.jpg",
    imageAlt: "Vintage buttons, buckles and haberdashery details",
    accent: "#3a3a2e",
  },
];

const SHOP_LABELS = Object.freeze(
  SHOPS.reduce((acc, shop) => {
    acc[shop.shopKey] = shop.name;
    return acc;
  }, {}),
);

function safeText(value = "") {
  return String(value ?? "").trim();
}

function getShopLabel(shopKey = "") {
  const normalizedShopKey = safeText(shopKey);
  return SHOP_LABELS[normalizedShopKey] || normalizedShopKey.replace(/-/g, " ");
}

function getNamedReferenceLabel(value) {
  if (!value) return "";

  if (typeof value === "string") {
    const text = safeText(value);
    return /^[a-f\d]{24}$/i.test(text) ? "" : text;
  }

  return safeText(value.name || value.title || value.label);
}

function getProductLabel(product = {}) {
  const shopLabel = getShopLabel(product.shopKey);
  const categoryLabel =
    getNamedReferenceLabel(product.category) ||
    getNamedReferenceLabel(product.subCategory || product.subcategory);

  return [shopLabel, categoryLabel].filter(Boolean).join(" / ") || "Shop";
}

function getProductPath(product = {}) {
  const shopKey = safeText(product.shopKey);
  const identifier = safeText(product.slug || product._id || product.id);

  if (!shopKey || !identifier) return "";

  return `/shop/${encodeURIComponent(shopKey)}/product/${encodeURIComponent(
    identifier,
  )}`;
}

function getProductKey(product = {}) {
  return safeText(product._id || product.id || product.slug);
}

function getProductImageAlt(product = {}) {
  const productName = safeText(product.name) || "Featured product";
  const brand = safeText(product.brand);

  return brand ? `${productName} by ${brand}` : productName;
}

function FeaturedProductCard({ product }) {
  const productName = safeText(product?.name) || "Untitled product";
  const productPath = getProductPath(product);
  const image = getProductImage(product);
  const displayPrice = getProductDisplayPrice(product);
  const productLabel = getProductLabel(product);

  return (
    <article className="shop-landing__product">
      <Link
        to={productPath}
        className="shop-landing__product-link"
        aria-label={`View ${productName}`}
      >
        <div className="shop-landing__product-image-wrap">
          {image ? (
            <ProductImage
              image={image}
              alt={getProductImageAlt(product)}
              className="shop-landing__product-image"
              sizes="(max-width: 760px) calc(50vw - 24px), (max-width: 1080px) calc(50vw - 42px), 320px"
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
            />
          ) : null}
        </div>

        <div className="shop-landing__product-info">
          <span className="shop-landing__product-shop">{productLabel}</span>

          <h3 className="shop-landing__product-name">{productName}</h3>

          <div className="shop-landing__product-bottom">
            {displayPrice ? (
              <p className="shop-landing__product-price">{displayPrice}</p>
            ) : null}

            <span className="shop-landing__product-arrow" aria-hidden="true">
              →
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

export default function ShopLandingScreen() {
  const dispatch = useDispatch();

  const {
    loading = false,
    error = null,
    products = [],
  } = useSelector((state) => state.productFeatured || {});

  const featuredProducts = useMemo(
    () =>
      (Array.isArray(products) ? products : [])
        .filter((product) => product && getProductPath(product))
        .slice(0, FEATURED_PRODUCT_LIMIT),
    [products],
  );

  useEffect(() => {
    dispatch(listFeaturedProducts({ limit: FEATURED_PRODUCT_LIMIT })).catch(
      () => undefined,
    );
  }, [dispatch]);

  return (
    <>
      <Meta
        title="Shop | Academy Costumes London"
        description="Explore three specialist departments: Ready-Made replica garments, archive textiles and fabrics, and haberdashery — buttons, buckles, braids and trims."
        image={HERO_IMAGE}
        url="/shop"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
        ]}
      />

      <main className="shop-landing">
        {/* PAGE HEADER */}
        <header className="shop-landing__header">
          <p className="shop-landing__eyebrow">Academy Costumes London</p>

          <h1 className="shop-landing__title">Choose Your Shop</h1>

          <p className="shop-landing__subtitle">
            Three specialist departments for makers, collectors and costume
            professionals.
          </p>
        </header>

        <div className="shop-landing__rule" aria-hidden="true" />

        {/* DEPARTMENTS */}
        <nav
          className="shop-landing__grid"
          aria-label="Academy Costumes London shop departments"
        >
          {DEPARTMENTS.map((dept, index) => (
            <article
              className="shop-landing__card"
              key={dept.key}
              style={{
                "--card-accent": dept.accent,
              }}
            >
              <Link
                to={dept.to}
                className="shop-landing__card-link"
                aria-label={`Shop ${dept.title} — ${dept.subtitle}`}
              >
                <div className="shop-landing__image-wrap">
                  <img
                    src={dept.image}
                    alt={dept.imageAlt}
                    className="shop-landing__image"
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                  />

                  <div
                    className="shop-landing__image-overlay"
                    aria-hidden="true"
                  />
                </div>

                <div className="shop-landing__card-body">
                  <div className="shop-landing__card-meta">
                    <span
                      className="shop-landing__card-index"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span className="shop-landing__card-subtitle">
                      {dept.subtitle}
                    </span>
                  </div>

                  <h2 className="shop-landing__card-title">{dept.title}</h2>

                  <p className="shop-landing__card-desc">{dept.description}</p>

                  <span className="shop-landing__card-cta" aria-hidden="true">
                    {dept.cta}

                    <span className="shop-landing__card-arrow">→</span>
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </nav>

        {/* FEATURED PRODUCTS */}
        <section
          className="shop-landing__featured"
          aria-labelledby="shop-featured-title"
        >
          <div className="shop-landing__featured-header">
            <div className="shop-landing__featured-heading">
              <p className="shop-landing__featured-eyebrow">
                Selected from the shops
              </p>

              <h2
                id="shop-featured-title"
                className="shop-landing__featured-title"
              >
                Featured Products
              </h2>
            </div>

            <Link
              to={SHOP_PATHS[READY_MADE_SHOP_KEY]}
              className="shop-landing__featured-view-all"
            >
              Browse the shops
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          {loading ? (
            <div
              className="shop-landing__featured-state shop-landing__featured-state--loading"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader />
            </div>
          ) : error ? (
            <div className="shop-landing__featured-state">
              <Message variant="danger">{error}</Message>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="shop-landing__featured-state">
              <Message variant="info">
                No featured products are available right now.
              </Message>
            </div>
          ) : (
            <div className="shop-landing__featured-grid">
              {featuredProducts.map((product) => (
                <FeaturedProductCard
                  key={getProductKey(product)}
                  product={product}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}
